import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GlobalLoaderPage } from '@/components/GlobalLoader';
import type { UserRole } from '@/lib/api';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Required role(s). User must have one of these. Default: any authenticated user. */
  role?: UserRole | UserRole[];
}

export function ProtectedRoute({ children, role }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <GlobalLoaderPage />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const allowedRoles = role == null ? (['admin', 'tutor', 'student'] as const) : Array.isArray(role) ? role : [role];
  if (!allowedRoles.includes(user.role)) {
    return (
      <div className="min-h-[40vh] flex flex-col items-center justify-center gap-4 px-6">
        <p className="text-destructive font-medium">You don’t have access to this page.</p>
        <p className="text-muted-foreground text-sm">Required role: {allowedRoles.join(' or ')}.</p>
        <p className="text-muted-foreground text-sm">If you need admin access, contact your administrator.</p>
      </div>
    );
  }

  return <>{children}</>;
}
